import { equalBytes } from '../bytes/encoding';
import { nodeHash } from './hash';
import { half, isOdd, isPowerOfTwo } from './math';

/** One hashing step in a proof, kept so the UI can draw the path. */
export interface ProofStep {
  /** Sibling hash supplied by the proof. */
  sibling: Uint8Array;
  /** Which side the sibling was on when hashing. */
  siblingSide: 'left' | 'right';
  /** Running hash before this step. */
  input: Uint8Array;
  /** Running hash after this step. */
  output: Uint8Array;
}

export interface InclusionResult {
  ok: boolean;
  computedRoot?: Uint8Array;
  steps: ProofStep[];
  error?: string;
}

/**
 * RFC 9162 §2.1.3.2 — verifies that `leaf` (already hashed) is at `leafIndex` in a tree of
 * `treeSize` leaves whose root is `expectedRoot`, using the audit path `proof`.
 */
export async function verifyInclusion(
  leaf: Uint8Array,
  leafIndex: number,
  treeSize: number,
  proof: Uint8Array[],
  expectedRoot?: Uint8Array,
): Promise<InclusionResult> {
  const steps: ProofStep[] = [];
  if (!Number.isSafeInteger(leafIndex) || leafIndex < 0) return { ok: false, steps, error: 'leaf index must be a non-negative integer' };
  if (leafIndex >= treeSize) return { ok: false, steps, error: `leaf index ${leafIndex} is not below tree size ${treeSize}` };
  if (leaf.length !== 32) return { ok: false, steps, error: 'leaf hash is not 32 bytes' };
  if (expectedRoot && expectedRoot.length !== 32) return { ok: false, steps, error: 'expected root is not 32 bytes' };
  let fn = leafIndex;
  let sn = treeSize - 1;
  let r = leaf;
  for (const p of proof) {
    if (sn === 0) return { ok: false, steps, error: 'proof has more nodes than the tree height allows' };
    if (p.length !== 32) return { ok: false, steps, error: 'proof node is not 32 bytes' };
    let out: Uint8Array;
    if (isOdd(fn) || fn === sn) {
      out = await nodeHash(p, r);
      steps.push({ sibling: p, siblingSide: 'left', input: r, output: out });
      if (!isOdd(fn)) {
        while (!isOdd(fn) && fn !== 0) {
          fn = half(fn);
          sn = half(sn);
        }
      }
    } else {
      out = await nodeHash(r, p);
      steps.push({ sibling: p, siblingSide: 'right', input: r, output: out });
    }
    r = out;
    fn = half(fn);
    sn = half(sn);
  }
  if (sn !== 0) return { ok: false, steps, computedRoot: r, error: 'proof has too few nodes for this tree size' };
  if (expectedRoot && !equalBytes(r, expectedRoot)) {
    return { ok: false, steps, computedRoot: r, error: 'computed root does not match the tree head' };
  }
  return { ok: true, steps, computedRoot: r };
}

export interface ConsistencyStep {
  node: Uint8Array;
  /** 'both' — hashed into old and new running hashes; 'new' — only into the new one. */
  applied: 'both' | 'new';
  side: 'left' | 'right';
  oldInput?: Uint8Array;
  oldOutput?: Uint8Array;
  newInput: Uint8Array;
  newOutput: Uint8Array;
}

export interface ConsistencyResult {
  ok: boolean;
  computedFirstRoot?: Uint8Array;
  computedSecondRoot?: Uint8Array;
  steps: ConsistencyStep[];
  /** Whether the first root was used as the implicit first proof node (first is a power of two). */
  firstRootPrepended: boolean;
  error?: string;
}

/**
 * RFC 9162 §2.1.4.2 — verifies that the tree of size `first` with root `firstRoot` is a prefix of
 * the tree of size `second` with root `secondRoot`, using consistency proof `proof`.
 */
export async function verifyConsistency(
  first: number,
  second: number,
  proof: Uint8Array[],
  firstRoot: Uint8Array,
  secondRoot: Uint8Array,
): Promise<ConsistencyResult> {
  const steps: ConsistencyStep[] = [];
  const base = { steps, firstRootPrepended: false };
  if (!Number.isSafeInteger(first) || !Number.isSafeInteger(second) || first < 0 || second < 0) {
    return { ...base, ok: false, error: 'tree sizes must be non-negative integers' };
  }
  if (first > second) return { ...base, ok: false, error: 'first tree size is larger than the second' };
  // Matches the reference verifier: a proof from the empty tree proves nothing, so refuse it.
  if (first === 0) return { ...base, ok: false, error: 'consistency from an empty tree (size 0) is meaningless' };
  if (first === second) {
    if (proof.length !== 0) return { ...base, ok: false, error: 'proof must be empty when the sizes are equal' };
    const ok = equalBytes(firstRoot, secondRoot);
    return { ...base, ok, computedFirstRoot: firstRoot, computedSecondRoot: secondRoot, error: ok ? undefined : 'roots differ' } as ConsistencyResult;
  }
  if (proof.length === 0) return { ...base, ok: false, error: 'proof is empty' };
  if (firstRoot.length !== 32 || secondRoot.length !== 32) return { ...base, ok: false, error: 'root hashes must be 32 bytes' };
  if (proof.some((p) => p.length !== 32)) return { ...base, ok: false, error: 'proof node is not 32 bytes' };

  const firstRootPrepended = isPowerOfTwo(first);
  const path = firstRootPrepended ? [firstRoot, ...proof] : proof;
  let fn = first - 1;
  let sn = second - 1;
  while (isOdd(fn)) {
    fn = half(fn);
    sn = half(sn);
  }
  let fr = path[0]!;
  let sr = path[0]!;
  for (const c of path.slice(1)) {
    if (sn === 0) return { steps, firstRootPrepended, ok: false, error: 'proof has more nodes than the tree height allows' };
    if (isOdd(fn) || fn === sn) {
      const nfr = await nodeHash(c, fr);
      const nsr = await nodeHash(c, sr);
      steps.push({ node: c, applied: 'both', side: 'left', oldInput: fr, oldOutput: nfr, newInput: sr, newOutput: nsr });
      fr = nfr;
      sr = nsr;
      if (!isOdd(fn)) {
        while (!isOdd(fn) && fn !== 0) {
          fn = half(fn);
          sn = half(sn);
        }
      }
    } else {
      const nsr = await nodeHash(sr, c);
      steps.push({ node: c, applied: 'new', side: 'right', newInput: sr, newOutput: nsr });
      sr = nsr;
    }
    fn = half(fn);
    sn = half(sn);
  }
  const result = { steps, firstRootPrepended, computedFirstRoot: fr, computedSecondRoot: sr };
  if (sn !== 0) return { ...result, ok: false, error: 'proof has too few nodes for this tree size' };
  if (!equalBytes(fr, firstRoot)) return { ...result, ok: false, error: 'computed first root does not match' };
  if (!equalBytes(sr, secondRoot)) return { ...result, ok: false, error: 'computed second root does not match' };
  return { ...result, ok: true };
}
